import React, { useContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Callout, DirectionalHint, Text } from '@fluentui/react';
import { HelpLink } from '@bingads-webui-react/help-link';
import { BRAND_REVIEW_STATUS, REVIEW_STATUS_TEXT } from './constants';

const getStyles = (_components, { palette }) => ({
  StatusText: {
    cursor: 'pointer',
    [`&.${BRAND_REVIEW_STATUS.PENDING}`]: {
      color: palette.themePrimary,
    },
    [`&.${BRAND_REVIEW_STATUS.REJECTED}`]: {
      color: palette.redDark,
      borderBottom: `1px dashed ${palette.themePrimary}`,
    },
    [`&.${BRAND_REVIEW_STATUS.APPROVED}`]: {
      color: palette.neutralSecondary,
      borderBottom: `1px dashed ${palette.themePrimary}`,
    },
  },
  StatusCallout: {
    width: 340,
    padding: 20,
    '& .status-callout-title': {
      display: 'block',
      fontSize: 20,
      fontWeight: 600,
      lineHeight: '28px',
      marginTop: 0,
      marginBottom: 8,
    },
    '& .status-callout-desp': {
      fontSize: 14,
    },
    '& .HelpTopic': {
      marginLeft: 6,
    },
  },
});

export const StatusText = ({ itemId, itemStatus, showCallout }) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);

  return (
    <Text
      className={classNames(classes.StatusText, 'status-text', itemStatus)}
      id={`status-${itemStatus}-${itemId}`}
      onMouseEnter={() => showCallout({ itemId, itemStatus })}
    >
      {i18n.getString(REVIEW_STATUS_TEXT[itemStatus])}
    </Text>
  );
};

StatusText.propTypes = {
  itemId: PropTypes.string.isRequired,
  itemStatus: PropTypes.string.isRequired,
  showCallout: PropTypes.func.isRequired,
};

export const StatusCallout = ({ itemId, itemStatus, visible, onDismiss }) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);

  const getStatusCalloutContent = () => {
    if (itemStatus === BRAND_REVIEW_STATUS.APPROVED) {
      return (
        <>
          <Text className="status-callout-title">{i18n.getString(_TL_('Eligible'))}</Text>
          <Text className="status-callout-desp">{i18n.getString(_TL_('The brand you requested has been approved and will be automatically added to the brand list and your shared library.'))}</Text>
        </>
      );
    } else if (itemStatus === BRAND_REVIEW_STATUS.REJECTED) {
      return (
        <>
          <Text className="status-callout-title">{i18n.getString(_TL_('Disapproved'))}</Text>
          <Text className="status-callout-desp">{i18n.getString(_TL_('The brand you requested wasn’t approved.'))}</Text>
          <HelpLink query="help:app54850">{i18n.getString(_TL_('Learn how to fix a disapproved brand. '))}</HelpLink>
        </>
      );
    }
    return (
      <>
        <Text className="status-callout-title">{i18n.getString(_TL_('Pending editorial review'))}</Text>
        <Text block className="status-callout-desp">{i18n.getString(_TL_('Your brand request is under review. Please note that you can only apply brand protection or brand exclusion to a brand once it’s approved.  If approved, the brand will be automatically added to the brand list and your shared library. If disapproved, the brand will be removed. You may also choose to delete this brand request.'))}</Text>
      </>
    );
  };

  return (
    visible && (
      <Callout
        className={classNames(classes.StatusCallout, 'status-callout')}
        target={`#status-${itemStatus}-${itemId}`}
        onDismiss={onDismiss}
        directionalHint={DirectionalHint.rightCenter}
        setInitialFocus
      >
        {getStatusCalloutContent()}
      </Callout>
    )
  );
};

StatusCallout.propTypes = {
  visible: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  itemId: PropTypes.string.isRequired,
  itemStatus: PropTypes.string.isRequired,
};
